import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface OrderRequest {
  restaurant_id: string;
  name: string;
  phone: string;
  email: string;
  special_requests: string;
  order_type: string;
  delivery_type: string;
  pay_now: boolean;
  ordered_items: Array<{
    name: string;
    quantity: number;
    itemPrice: string;
    modifiers: Array<{
      modifier_name: string;
      modifier_price: number;
    }>;
    modifier_price: string;
    spice_level: string;
    image: string;
    total_item_price: string;
  }>;
  grand_total: string;
}

export interface OrderResponse {
  message: string;
  payment_link?: string;
  session_id?: string;
}

export interface PaymentStatusRequest {
  order_id: string;
}

export interface PaymentStatusResponse {
  order_id: string;
  status: string;
  payment_status: string;
  order_details?: any;
  created_at?: string;
  updated_at?: string;
}

interface OrderState {
  loading: boolean;
  orderResponse: OrderResponse | null;
  error: string | null;
  paymentStatusLoading: boolean;
  paymentStatusResponse: PaymentStatusResponse | null;
  paymentStatusError: string | null;
}

const initialState: OrderState = {
  loading: false,
  orderResponse: null,
  error: null,
  paymentStatusLoading: false,
  paymentStatusResponse: null,
  paymentStatusError: null,
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    placeOrderRequest: (state, action: PayloadAction<OrderRequest>) => {
      state.loading = true;
      state.error = null;
      state.orderResponse = null;
    },
    
    placeOrderSuccess: (state, action: PayloadAction<OrderResponse>) => {
      state.loading = false;
      state.orderResponse = action.payload;
      state.error = null;
    },
    
    placeOrderFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.orderResponse = null;
    },
    
    clearOrderState: (state) => {
      state.loading = false;
      state.orderResponse = null;
      state.error = null;
    },
    
    fetchPaymentStatusRequest: (state, action: PayloadAction<PaymentStatusRequest>) => {
      state.paymentStatusLoading = true;
      state.paymentStatusError = null;
      state.paymentStatusResponse = null;
    },
    
    fetchPaymentStatusSuccess: (state, action: PayloadAction<PaymentStatusResponse>) => {
      state.paymentStatusLoading = false;
      state.paymentStatusResponse = action.payload;
      state.paymentStatusError = null;
    },
    
    fetchPaymentStatusFailure: (state, action: PayloadAction<string>) => {
      state.paymentStatusLoading = false;
      state.paymentStatusError = action.payload;
      state.paymentStatusResponse = null;
    },
    
    clearPaymentStatusState: (state) => {
      state.paymentStatusLoading = false;
      state.paymentStatusResponse = null;
      state.paymentStatusError = null;
    },
  },
});

export const {
  placeOrderRequest,
  placeOrderSuccess,
  placeOrderFailure,
  clearOrderState,
  fetchPaymentStatusRequest,
  fetchPaymentStatusSuccess,
  fetchPaymentStatusFailure,
  clearPaymentStatusState,
} = orderSlice.actions;

export default orderSlice.reducer;
